import { useFormContext } from "react-hook-form";
import InputField from "@medoma/components/InputField/InputField";
import TextArea from "@/components/TextArea/TextArea";
import {
  informationFieldConfigValueSchema,
  type IInformationFieldConfigValue,
  useCareHub,
} from "@/api/Patients";
import { useCallback, useEffect, useState } from "react";
import { fetchCoordinates } from "@/api/Mapbox";
import Fieldset from "@medoma/components/Fieldset/Fieldset";
import { MapProvider, Marker, useMap } from "react-map-gl/mapbox";
import Map, { type ICoordinatePair } from "@/components/Map/Map";
import { getValidCoordinates } from "@/forms/coordinatesUtils";
import Form from "@/components/Form/Form";
import {
  conventionalPostalCodePattern,
  conventionalPostalCodeSchema,
  internalizePostalCode,
} from "@medoma/models/postalCode";
import { useUserProfile } from "@/api/Users";
import { useLingui } from "@lingui/react/macro";
import type { SiteId } from "@medoma/models/sites";

const PREFERRED_EDIT_ADDRESS_ZOOM = 13;
const MAX_DISTANCE_FROM_CARE_HUB_IN_METERS = 150_000;

// Accepts a decimal comma as well as a point. An empty field becomes NaN rather than 0, so
// validation reports it instead of reading it as a coordinate.
const decimalFromInput = (value: string | number) => {
  if (typeof value === "number") {
    return value;
  }
  return value.trim() === "" ? NaN : Number(value.replace(",", "."));
};

export type AddressInputFields = {
  addressLine1: string;
  postalCode: string;
  city: string;
  additionalAddressInformation?: string;
  keyToPatient?: string;
  coordinates: {
    longitude: number;
    latitude: number;
  };
};

const AddressInputWithMap = ({
  siteId,
  additionalAddressInformationFieldConfig,
  keyToPatientFieldConfig,
}: {
  siteId: SiteId;
  additionalAddressInformationFieldConfig: IInformationFieldConfigValue;
  keyToPatientFieldConfig: IInformationFieldConfigValue;
}) => {
  const {
    register,
    formState: { dirtyFields, errors },
    watch,
    setValue,
  } = useFormContext<AddressInputFields>();

  const { data: careHub } = useCareHub(siteId);

  const setCoordinates = useCallback(
    (coordinates: ICoordinatePair) =>
      setValue("coordinates", coordinates, {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      }),
    [setValue],
  );

  const { registerPatientMap } = useMap();

  const addressLine1 = watch("addressLine1");
  const postalCode = watch("postalCode");
  const coordinates = watch("coordinates");

  const coordinatesProvided =
    Boolean(coordinates.latitude && coordinates.longitude) ||
    Boolean(
      dirtyFields.coordinates?.latitude || dirtyFields.coordinates?.longitude,
    );

  const [coordinatesFailedToFetch, setCoordinatesFailedToFetch] =
    useState(false);
  useEffect(() => {
    if (!registerPatientMap) {
      return;
    }
    if (!careHub) {
      return;
    }

    const validCoordinates = getValidCoordinates({
      longitude: coordinates.longitude,
      latitude: coordinates.latitude,
      careHub: careHub,
      maxDistance: MAX_DISTANCE_FROM_CARE_HUB_IN_METERS,
    }) || { longitude: careHub.longitude, latitude: careHub.latitude };

    if (validCoordinates) {
      registerPatientMap.easeTo({
        center: [validCoordinates.longitude, validCoordinates.latitude],
        duration: 1000,
        zoom: PREFERRED_EDIT_ADDRESS_ZOOM,
      });
    }
  }, [
    registerPatientMap,
    coordinates.latitude,
    coordinates.longitude,
    careHub,
  ]);

  const { countryCode } = useUserProfile();
  useEffect(() => {
    const handler = setTimeout(() => {
      if (
        addressLine1 &&
        postalCode &&
        conventionalPostalCodeSchema.shape[countryCode].safeParse(postalCode)
          .success
      ) {
        fetchCoordinates({
          addressLine1,
          countryCode,
          postalCode: internalizePostalCode(
            conventionalPostalCodeSchema.shape[countryCode].parse(postalCode),
            countryCode,
          ),
          siteId,
        })
          .then((coordinates) => {
            setCoordinates(coordinates);
            setCoordinatesFailedToFetch(false);
          })
          .catch(() => {
            setCoordinatesFailedToFetch(true);
          });
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [addressLine1, postalCode, setCoordinates, countryCode, siteId]);

  const { t } = useLingui();

  return (
    <Fieldset legend={t`Adress`} visualStyle="framed">
      <Form.Section>
        <InputField
          label={t`Gata`}
          showOptionalLabel={false}
          errorMessage={errors.addressLine1?.message}
          {...register("addressLine1", {
            required: { value: true, message: t`Ange gata` },
          })}
        />
        <Form.Row>
          <InputField
            label={t`Postnummer`}
            showOptionalLabel={false}
            errorMessage={errors.postalCode?.message}
            {...register("postalCode", {
              required: { value: true, message: t`Ange postnummer` },
              pattern: {
                value: conventionalPostalCodePattern[countryCode].value,
                message: t(conventionalPostalCodePattern[countryCode].message),
              },
            })}
          />
          <InputField
            label={t`Postort`}
            showOptionalLabel={false}
            errorMessage={errors.city?.message}
            {...register("city", {
              required: { value: true, message: t`Ange postort` },
            })}
          />
        </Form.Row>

        <Fieldset
          legend={
            !coordinatesProvided && !coordinatesFailedToFetch
              ? t`Fyll i adress för att se plats`
              : coordinatesFailedToFetch
                ? t`Kunde inte ladda kartan. Fyll i koordinater manuellt.`
                : t`Plats`
          }
          disabled={!coordinatesProvided && !coordinatesFailedToFetch}
        >
          <Form.Section>
            <Map
              initialAreaToShow={{
                center: {
                  lat: coordinates.latitude,
                  lng: coordinates.longitude,
                },
                zoom: PREFERRED_EDIT_ADDRESS_ZOOM,
              }}
              disabledHandlers={coordinatesProvided ? "scrollZoom" : "all"}
              id="registerPatientMap"
              style={{ height: "30vh" }}
            >
              {coordinatesProvided &&
              !coordinatesFailedToFetch &&
              coordinates.longitude &&
              coordinates.latitude ? (
                (() => {
                  if (!careHub) {
                    return <></>;
                  }
                  const validCoordinates = getValidCoordinates({
                    longitude: coordinates.longitude,
                    latitude: coordinates.latitude,
                    careHub: careHub,
                    maxDistance: MAX_DISTANCE_FROM_CARE_HUB_IN_METERS,
                  });
                  if (validCoordinates) {
                    return (
                      <Marker
                        longitude={validCoordinates.longitude}
                        latitude={validCoordinates.latitude}
                      />
                    );
                  } else {
                    return <></>;
                  }
                })()
              ) : (
                <></>
              )}
            </Map>
            <Form.Row>
              <InputField
                label={t`Longitud`}
                showOptionalLabel={false}
                errorMessage={errors.coordinates?.longitude?.message}
                {...register("coordinates.longitude", {
                  required: {
                    value: true,
                    message: t`Longitud behöver anges`,
                  },
                  min: {
                    value: -180,
                    message: t`Longitud bör vara över -180`,
                  },
                  max: {
                    value: 180,
                    message: t`Longitud bör vara under 180`,
                  },
                  setValueAs: decimalFromInput,
                  validate: {
                    isNumber: (longitude) =>
                      Number.isFinite(longitude) ||
                      t`Ange longitud som ett decimaltal`,
                    isPosition: (longitude, { coordinates }) =>
                      longitude !== 0 ||
                      coordinates.latitude !== 0 ||
                      t`Fyll i gata och postnummer, eller ange koordinater manuellt`,
                  },
                })}
              />
              <InputField
                label={t`Latitud`}
                showOptionalLabel={false}
                errorMessage={errors.coordinates?.latitude?.message}
                {...register("coordinates.latitude", {
                  required: {
                    value: true,
                    message: t`Latitud behöver anges`,
                  },
                  min: {
                    value: -90,
                    message: t`Latitud bör vara över -90`,
                  },
                  max: {
                    value: 90,
                    message: t`Latitud bör vara under 90`,
                  },
                  setValueAs: decimalFromInput,
                  validate: (latitude) =>
                    Number.isFinite(latitude) ||
                    t`Ange latitud som ett decimaltal`,
                })}
              />
            </Form.Row>
          </Form.Section>
        </Fieldset>

        {additionalAddressInformationFieldConfig ===
        informationFieldConfigValueSchema.enum.hidden ? null : (
          <TextArea
            label={t`Övrig adressinformation`}
            showOptionalLabel={
              additionalAddressInformationFieldConfig ===
              informationFieldConfigValueSchema.enum.optional
            }
            {...register("additionalAddressInformation", {
              required: {
                value:
                  additionalAddressInformationFieldConfig ===
                  informationFieldConfigValueSchema.enum.required,
                message: t`Ange övrig adressinformation`,
              },
            })}
            formatHint={t`T.ex portkod, parkering`}
            errorMessage={errors.additionalAddressInformation?.message}
          />
        )}
        {keyToPatientFieldConfig ===
        informationFieldConfigValueSchema.enum.hidden ? null : (
          <InputField
            type="text"
            label={t`Nyckel`}
            showOptionalLabel={
              keyToPatientFieldConfig ===
              informationFieldConfigValueSchema.enum.optional
            }
            {...register("keyToPatient", {
              required: {
                value:
                  keyToPatientFieldConfig ===
                  informationFieldConfigValueSchema.enum.required,
                message: t`Ange information om nyckel`,
              },
            })}
            errorMessage={errors.keyToPatient?.message}
          />
        )}
      </Form.Section>
    </Fieldset>
  );
};

const AddressInput = (props: {
  siteId: SiteId;
  additionalAddressInformationFieldConfig: IInformationFieldConfigValue;
  keyToPatientFieldConfig: IInformationFieldConfigValue;
}) => {
  return (
    <MapProvider>
      <AddressInputWithMap {...props} />
    </MapProvider>
  );
};

export default AddressInput;
